import React, { useMemo, useCallback } from "react";
import { DEFAULT_CURRENT_PAGE, DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from "../constants/sidekick";
import { PageAction } from "../types/sidekick";

/**
 * Result interface for the useDataPagination hook
 */
export interface UseDataPaginationResult<T> {
    data: T[];
    currentItems: T[];
    currentPage: number;
    rowsPerPage: number;
    rowsPerPageOptions: number[];
    totalItems: number;
    totalPages: number;
    indexOfFirstItem: number;
    indexOfLastItem: number;
    handleFirstPage: () => void;
    handlePrevPage: () => void;
    handleNextPage: () => void;
    handleLastPage: () => void;
    handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    isLoading: boolean;
    error: string | null;
}

/**
 * Props for the useDataPagination hook
 *
 * This interface supports Redux-based pagination.
 */
export interface UseDataPaginationProps<T> {
    data: T[];
    totalCount: number;
    loading: boolean;
    error: string | null;
    currentPage?: number;
    rowsPerPage?: number;
    onPageChange?: (page: number, pageAction: PageAction) => void;
    onRowsPerPageChange?: (rowsPerPage: number) => void;
}

/**
 * Hook for managing pagination with Redux
 *
 * This hook provides pagination functionality using Redux for state management.
 * It calculates pagination values and provides handlers for pagination actions.
 */
export function useDataPagination<T>({
    data,
    totalCount,
    loading,
    error,
    currentPage = DEFAULT_CURRENT_PAGE,
    rowsPerPage = DEFAULT_ROWS_PER_PAGE,
    onPageChange,
    onRowsPerPageChange
}: UseDataPaginationProps<T>): UseDataPaginationResult<T> {
    // Memoize pagination calculations to prevent unnecessary recalculations
    const totalItems = useMemo(() => totalCount, [totalCount]);

    const totalPages = useMemo(() =>
        Math.ceil(totalItems / rowsPerPage),
        [totalItems, rowsPerPage]
    );

    const indexOfLastItem = useMemo(() =>
        currentPage * rowsPerPage,
        [currentPage, rowsPerPage]
    );

    const indexOfFirstItem = useMemo(() =>
        indexOfLastItem - rowsPerPage,
        [indexOfLastItem, rowsPerPage]
    );

    // Filter data based on pagination parameters
    const currentItems = useMemo(() => {
        // For server-side pagination, the data should already be paginated
        // For client-side pagination, slice the data based on current page and rows per page
        if (data.length > rowsPerPage) {
            return data.slice(indexOfFirstItem, indexOfLastItem);
        }
        return data;
    }, [data, indexOfFirstItem, indexOfLastItem, rowsPerPage]);

    // Pagination handlers that dispatch Redux actions
    const handleFirstPage = useCallback(() => {
        if (onPageChange) {
            onPageChange(1, 'first');
        }
    }, [onPageChange]);

    const handlePrevPage = useCallback(() => {
        if (onPageChange) {
            const newPage = Math.max(currentPage - 1, 1);
            onPageChange(newPage, 'previous');
        }
    }, [currentPage, onPageChange]);

    const handleNextPage = useCallback(() => {
        if (onPageChange) {
            const newPage = Math.min(currentPage + 1, totalPages);
            onPageChange(newPage, 'next');
        }
    }, [currentPage, totalPages, onPageChange]);

    const handleLastPage = useCallback(() => {
        if (onPageChange) {
            onPageChange(totalPages, 'last');
        }
    }, [totalPages, onPageChange]);

    // Rows per page handler
    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        if (onRowsPerPageChange) {
            onRowsPerPageChange(newRowsPerPage);
        }
        event.target.blur();
    }, [onRowsPerPageChange]);

    // Memoize the entire return object to prevent unnecessary re-renders
    return useMemo(() => ({
        data,
        currentItems,
        currentPage,
        rowsPerPage,
        rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
        totalItems,
        totalPages,
        indexOfFirstItem,
        indexOfLastItem,
        handleFirstPage,
        handlePrevPage,
        handleNextPage,
        handleLastPage,
        handleChangeRowsPerPage,
        isLoading: loading,
        error
    }), [
        data,
        currentItems,
        currentPage,
        rowsPerPage,
        totalItems,
        totalPages,
        indexOfFirstItem,
        indexOfLastItem,
        handleFirstPage,
        handlePrevPage,
        handleNextPage,
        handleLastPage,
        handleChangeRowsPerPage,
        loading,
        error
    ]);
}
